import Base from "./Base"
import Group from "./Group"
import VibiaLightModel from "./Pendant"
import { ComposableElement } from "./Group"
import DomainFactory from "../lib/DomainFactory"
import Light from "./Light"
import Table from '../data/Table'
import { getModifier } from "../catalog/ArticleCollection"
import { baseAttribute, groupAttributes } from "./composition"
import Toggles from "@domain/toggles/Toggles"

export interface ClusterElement<T extends Base = Base> extends ComposableElement<T> {
	options: {
		position: number[]
	}
}

export default class Cluster extends Group {
	// CLuster != Canopy
	// Cluster = Canopy + Wire(s) + Light(s)
	// this.size = Not used. It is dynamic and not persisted for this class

	static className = 'Cluster'
	type = 'Cluster';
	constructor(elements: ComposableElement[] | Group, parent?, virtual?, attributes?: groupAttributes) {
		super(elements, parent, virtual, null, attributes)
		if (attributes?.installation) {
			this.props.attributes.installation = attributes?.installation
		} else if (virtual) {
			this.props.attributes.installation = 'virtual'
		}

		if (Toggles.isEnabled("logDomainConstructor")) {
			console.log(`%c[Cluster]`, '🍀;background: lightgreen; color: #444;padding: 3px; border-radius: 5px;', '🍀')
		}
	}

	addElement(newClusterElement: ComposableElement | VibiaLightModel, prevId?: string) {
		if (!this.getElement(newClusterElement) && !this.isEmpty() && !this.isFreeWire()) {
			throw "Error while adding a new light to the cluster: Cluster allows only one light per cluster"
		}
		return new Cluster(super.addElement(newClusterElement, prevId), this.getParent(), this.virtual, this.getAttributes(true))
	}

	getElements() {
		return super.getElements() as ClusterElement[]
	}

	getElement(element: Base | ComposableElement | string): ClusterElement {
		return super.getElement(element) as ClusterElement
	}

	// TODO: Deprecate
	getCode() {
		return this.getAttribute('installation', true)
	}

	isFreeWire() {
		return this.getAttributeModifier('installation') == '18'
	}

	isNewAttributeValid(attribute: string, newValue: any): boolean {
		if (attribute == 'installation') {
			return (this.getElements() as ComposableElement[]).every((element) => element.element.isNewAttributeValid(attribute, newValue))
		}
		return super.isNewAttributeValid(attribute, newValue)
	}

	setAttribute(attribute: baseAttribute, value, own?) {
		if (this.isNewAttributeValid(attribute, value)) {
			return super.setAttribute(attribute, value, attribute == 'installation' ? true : own)
		}
		return this
	}

	getCanopy() {
		const light = this.getLights()[0]?.element as Light
		if (!light) return null
		const canopies = light.getRenderAttribute('canopy') ?? {}
		const installationModifier = this.getAttributeModifier('installation')
		const canopy = canopies[installationModifier]
		return canopy
	}

	getSize(total: boolean = true) {
		if (total && (Toggles.isEnabled("enableFreeWire") && !this.isFreeWire())) {
			let childrenSize = { height: 0, width: 0, depth: 0 }
			for (const { element: child } of this.getElements()) {
				const childSize = child.getSize(true)
				childrenSize = {
					height: Math.max(childrenSize.height, childSize.height),
					width: Math.max(childrenSize.width, childSize.width),
					depth: Math.max(childrenSize.depth, childSize.depth),
				}
			}
			const canopySize = this.getCanopySize()
			return {
				height: Math.max(canopySize.height, childrenSize.height),
				width: Math.max(canopySize.width, childrenSize.width),
				depth: Math.max(canopySize.depth, childrenSize.depth),
			}
		} else {
			return this.getCanopySize()
		}
	}

	getCanopySize() {
		const canopy = this.getCanopy()
		return canopy?.size || { height: 0, width: 0, depth: 0 }
	}

	//gets compatible canopies with vibia + parents restrictions (compositive / room)
	// TODO: Refactor (Take all children into consideration & canopy.compatibleInstallation restrictions)
	getAvailableCanopies() {
		if (!this.isEmpty() && this.getElements()[0]) {
			const compatibleClusters = ((this.getElements()[0].element) as Light).getLightOptions().canopy
			const clusterInstallations = Object.keys(compatibleClusters).map(key => compatibleClusters[key].reference)
			const validClusterInstallations = clusterInstallations.filter((installation) => this.requestSetAttribute('installation', installation, this) == installation)
			const finalClusters = {}
			validClusterInstallations.forEach(installation => finalClusters[getModifier(installation)] = compatibleClusters[getModifier(installation)])
			return finalClusters
		} else {
			return {}
		}
	}

	// TODO: must create UNIT TEST
	getLightOptions() {
		if (!this.isEmpty() && this.getElements()[0]) {
			return ((this.getElements()[0].element) as Light).getLightOptions()
		} else {
			return {}
		}
	}

	getOrbitTarget(absolute: any, self?): number[] {
		if (self && !this.virtual) {
			return super.getOrbitTarget(absolute)
		}
		const box = this.getBoundingBox()
		return [box.x, box.y - (box.height / 2), box.z]
	}

	getAbsolutePosition(child?: Base): number[] {
		const position = super.getAbsolutePosition(child)
		const canopySize = this.getCanopySize()
		return position ? [position[0], position[1] - canopySize?.height, position[2]] : null
	}

	//TODO:: Esta funcion deberia ser refactorizada, ya que se deberia de llamar a su padre para que pueda o no moverse, y ahora mismo puede darse el caso de que se mueva X veces por hijo que tenga
	moveElements(x, y, z): this {
		return Base.prototype.move.call(this, x, y, z);
	}

	//TODO:: Esta funcion deberia ser refactorizada, ya que se deberia de llamar a su padre para que pueda o no moverse, y ahora mismo puede darse el caso de que se mueva X veces por hijo que tenga
	rotate2DElements(rotationCenterX, rotationCenterZ, angle): this {
		return Base.prototype.rotate2DElements.call(this, rotationCenterX, rotationCenterZ, angle);
	}

	fromJSON(json, options?: any, instance?: Base) {
		return Cluster.fromJSON(json, options, instance)
	}

	static fromJSON(json, options?: any, instance?: Base) {
		const group = Group.fromJSON(json, { ...options, domainFactory: options?.domainFactory ?? new DomainFactory }, null)
		const attributes = json?.props?.attributes?.installation ? { installation: json?.props?.attributes?.installation } : null
		return super.fromJSON(json, { ...options, domainFactory: options?.domainFactory ?? new DomainFactory }, instance ?? new Cluster(group, null, null, attributes))
	}

	static dataFactory(data?, children?: any | any[]) {
		return {
			"element": {
				"type": "Cluster",
				"props": {
					"attributes": {
						"installation": data.installation ?? "10 Surface Basic",
						"position": [data.width ?? 0, data.height ?? 0, data.depth ?? 0]
					},
					"elements": children && children.length ? children : [children]
				},
				"virtual": null
			},
			"options": {
				"position": [data.width ?? 0, data.height ?? 0, data.depth ?? 0],
				"tension": data.tension ?? 0
			}
		}
	}
}